<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{ error: NuxtError<{ navigationContext?: NavigationContext, customDescription?: string, nsRestrictedLaw?: boolean }> }>()

watch(() => props.error?.data?.navigationContext, (context) => {
  if (context) {
    setNavigationContext(context)
  }
}, {
  immediate: true,
})

updateAppConfig({
  colorMode: 'light',
  theme: 'blurple',
})

const content = computed(() => {
  const code = props.error?.statusCode

  if (props.error?.data?.customDescription) {
    return {
      title: code,
      description: props.error.data.customDescription,
    }
  }

  switch (code) {
    case 403:
      return {
        title: code,
        subtitle: 'Dette innholdet er ikke inkludert i abonnementet ditt.',
        description: 'Ta kontakt med kundeservice for å kjøpe abonnement.',
      }
    case 401:
      return {
        title: code,
        subtitle: 'Dette innholdet krever abonnement.',
        description: 'Logg inn for å se om du har tilgang. Hvis ikke, ta kontakt med kundeservice for å kjøpe abonnement.',
      }
    case 404: {
      return {
        title: code,
        subtitle: 'Å nei... noe gikk galt',
        description: 'Sjekk om URL-en er riktig og prøv å laste ned siden på nytt',
      }
    }
    default:
      return {
        title: code,
        subtitle: 'Å nei... vi har problemer med serveren',
        description: 'Last inn siden på nytt, eller prøv igjen senere',
      }
  }
})

const useNsIllustration = computed(() => props.error?.statusCode === 403 && Boolean(props.error?.data?.nsRestrictedLaw))

useHead({
  bodyAttrs: {
    class: 'bg-white',
  },
})
</script>

<template>
  <LayoutContainer>
    <Header />
    <ContentDiv is="main" class="flex-1 flex items-center justify-center">
      <div v-if="useNsIllustration" class="flex flex-col items-center gap-m max-w-[680px] mx-auto px-m text-center">
        <div class="flex items-center justify-center w-[226px] h-[226px] sm:w-[300px] sm:h-[300px]">
          <IllustrationNotAuthorizedNS />
        </div>
        <div
          class="
          font-body
          text-sm
          text-center
          [&_p]:my-s
          first:[&_p]:mt-0
          last:[&_p]:mb-6
          leading-5
          "
          v-html="content.description"
        />
        <Button variant="secondary" size="medium" to="/produktkatalog/juridika-norsk-standard" class="w-full max-w-[240px]">
          Se&nbsp;abonnement
        </Button>
      </div>
      <div v-else class="flex flex-col md:flex-row gap-[2.5rem] md:gap-3xl max-w-[780px] mx-auto">
        <div class="flex mx-auto items-center justify-center w-[226px] h-[226px] sm:w-[300px] sm:h-[300px]">
          <IllustrationVector />
        </div>
        <div class="flex flex-col gap-2xs items-center md:items-start justify-center max-w-[400px]">
          <div class="flex flex-col gap-2xs">
            <h1 class="title-xl text-center md:text-left">
              {{ content.title }}
            </h1>
            <p class="text-3xl sm:text-6xl font-light">
              {{ content.subtitle }}
            </p>
          </div>
          <p class="font-heading font-light text-xl sm:text-2xl text-center md:text-left [&_*]:!text-xl sm:[&_*]:!text-2xl" v-html="content.description" />
        </div>
      </div>
    </ContentDiv>
  </LayoutContainer>
</template>
